// Deterministic avatar color + initials utilities.
//
// Two design goals:
//   1. The same user always gets the same color across every view (boards,
//      lists, team page, assignee bubbles, impersonation banner).
//   2. Colors are readable on white backgrounds and visually distinct from
//      each other — we keep an 8-color palette picked for contrast against
//      enterprise slate/gray UI chrome.
//
// Consumer shape:
//   getAvatarStyle(userId | name) → { bg, className } where className is a
//   ready-to-use Tailwind class stack. We expose the class names (and not
//   raw hex) so dark-mode support can be added later via a single palette
//   swap rather than chasing inline styles.

const PALETTE = [
  { bg: 'bg-indigo-500',  ring: 'ring-indigo-100' },
  { bg: 'bg-emerald-500', ring: 'ring-emerald-100' },
  { bg: 'bg-amber-500',   ring: 'ring-amber-100' },
  { bg: 'bg-rose-500',    ring: 'ring-rose-100' },
  { bg: 'bg-cyan-500',    ring: 'ring-cyan-100' },
  { bg: 'bg-violet-500',  ring: 'ring-violet-100' },
  { bg: 'bg-teal-500',    ring: 'ring-teal-100' },
  { bg: 'bg-orange-500',  ring: 'ring-orange-100' },
];

// FNV-1a 32-bit hash — stable, fast, zero deps. Output is bucketed into the
// palette length.
function hashKey(key = '') {
  let h = 0x811c9dc5;
  const s = String(key);
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = (h + ((h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24))) >>> 0;
  }
  return h;
}

export function getAvatarStyle(key) {
  if (!key) return { bg: 'bg-slate-400', ring: 'ring-slate-100' };
  return PALETTE[hashKey(key) % PALETTE.length];
}

// Initials: first + last name letter. Handles single-token names
// ("Avishek" → "AV"), email addresses ("t.saha@x.com" → "TS"), and falls
// back to a single letter if nothing usable exists.
export function getInitials(name = '') {
  const raw = String(name || '').trim();
  if (!raw) return '?';
  // Email → use the part before @.
  const cleaned = raw.includes('@') ? raw.split('@')[0].replace(/[._-]+/g, ' ') : raw;
  const tokens = cleaned.split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return '?';
  if (tokens.length === 1) {
    const t = tokens[0];
    return (t.slice(0, 2)).toUpperCase();
  }
  return (tokens[0][0] + tokens[tokens.length - 1][0]).toUpperCase();
}
